import { createBrowserRouter } from 'react-router-dom';
import WelcomeExperience from './components/welcome/WelcomeExperience';
import TransferRegrets from './components/loyalty/TransferRegrets';
import CaptainHindsight from './components/captain/CaptainHindsight';
import DifferentialKing from './components/differential/DifferentialKing';
import LeagueOverview from './components/league/LeagueOverview';
import LeagueConstellation from './components/league/LeagueConstellation';
import Navigation from './components/navigation/Navigation';
import MyJourney from './components/my-journey/MyJourney';
import RankPressureWave from './components/my-journey/RankPressureWave';
import BenchHaunting from './components/my-journey/BenchHaunting';
import SquadLifecycle from './components/my-journey/SquadLifecycle';
import ErrorBoundary, { withErrorBoundary } from './components/shared/ErrorBoundary';

// Wrap all components with error boundaries
const EnhancedWelcomeExperience = withErrorBoundary(WelcomeExperience);
const EnhancedTransferRegrets = withErrorBoundary(TransferRegrets);
const EnhancedCaptainHindsight = withErrorBoundary(CaptainHindsight);
const EnhancedDifferentialKing = withErrorBoundary(DifferentialKing);
const EnhancedLeagueOverview = withErrorBoundary(LeagueOverview);
const EnhancedLeagueConstellation = withErrorBoundary(LeagueConstellation);
const EnhancedMyJourney = withErrorBoundary(MyJourney);
const EnhancedRankPressureWave = withErrorBoundary(RankPressureWave);
const EnhancedBenchHaunting = withErrorBoundary(BenchHaunting);
const EnhancedSquadLifecycle = withErrorBoundary(SquadLifecycle);

// Layout component that includes navigation.
// Navigation renders a fixed top bar (56px). The placeholder div reserves that space in
// normal flow so page content fills exactly the remaining viewport height.
const WithNavigation = ({ children }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Placeholder reserves space for the fixed nav bar */}
      <div style={{ height: '56px', flexShrink: 0 }} />
      <Navigation />
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
};

// Create routes with navigation
const createRouteWithNavigation = (Component, additionalProps = {}) => {
  return {
    element: <WithNavigation><Component {...additionalProps} /></WithNavigation>,
    errorElement: <ErrorBoundary>
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-purple-800 to-indigo-900 text-white p-6">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md">
          <h2 className="text-2xl font-bold mb-4">Navigation Error</h2>
          <p className="mb-4">Something went wrong while trying to navigate to this page.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
          >
            Go to Home
          </button>
        </div>
      </div>
    </ErrorBoundary>
  };
};

// Routes configuration with error boundaries and loaders
const router = createBrowserRouter([
  {
    path: '/',
    element: <EnhancedWelcomeExperience />,
    errorElement: <ErrorBoundary>
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-purple-800 to-indigo-900 text-white p-6">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md">
          <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
          <p className="mb-4">We encountered an error while loading the welcome experience.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
          >
            Reload
          </button>
        </div>
      </div>
    </ErrorBoundary>
  },
  {
    path: '/journey-selector',
    element: <EnhancedWelcomeExperience />,
    errorElement: <ErrorBoundary />
  },
  // My Journey section
  {
    path: '/my-journey',
    ...createRouteWithNavigation(EnhancedMyJourney)
  },
  {
    path: '/my-profile',
    ...createRouteWithNavigation(EnhancedMyJourney, { activeTab: 'profile' })
  },
  // My Journey new features
  {
    path: '/rank-pressure',
    ...createRouteWithNavigation(EnhancedRankPressureWave)
  },
  {
    path: '/bench-haunting',
    ...createRouteWithNavigation(EnhancedBenchHaunting)
  },
  {
    path: '/squad-lifecycle',
    ...createRouteWithNavigation(EnhancedSquadLifecycle)
  },
  // Features section
  {
    path: '/transfer-regrets',
    ...createRouteWithNavigation(EnhancedTransferRegrets)
  },
  {
    path: '/captain-hindsight',
    ...createRouteWithNavigation(EnhancedCaptainHindsight)
  },
  {
    path: '/differential-king',
    ...createRouteWithNavigation(EnhancedDifferentialKing)
  },
  // League section
  {
    path: '/league-awards',
    ...createRouteWithNavigation(EnhancedMyJourney, { activeTab: 'awards' })
  },
  {
    path: '/league-overview',
    ...createRouteWithNavigation(EnhancedLeagueOverview)
  },
  {
    path: '/league-constellation',
    ...createRouteWithNavigation(EnhancedLeagueConstellation)
  },
  // Catch-all route for 404s
  {
    path: '*',
    element: <ErrorBoundary>
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-purple-800 to-indigo-900 text-white p-6">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4">Page Not Found</h2>
          <p className="mb-6">The page you're looking for doesn't exist.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full font-semibold tracking-wide"
          >
            Go to Home
          </button>
        </div>
      </div>
    </ErrorBoundary>
  }
]);

export default router;